import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    PageTitle,
    Modal,
    Input,
    Tooltip,
    Loading
} from '../root/components/common'
import { NavLink, useLocation, withRouter } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
    usersRequest,
    usersSuccess,
    userSuccess,
    rolesRequest,
    rolesRequestSuccess,
    customersRequest,
    customersRequestSuccess
} from './actions'
import Clear from '@material-ui/icons/Clear'
import { getNewUser, getRoleName } from '../../utils'
// modals
import DeleteUsersModal from './modals/delete'
import AddFromCustomerModal from './modals/add-from-customer'
import { createSelector } from 'reselect'
import { getCustomerOptions } from './selectors'
import { TableVirtualized } from '../root/components/common/table-virtualized/table'
import { UserPermissions } from '../../services/permissions'
import { Box, IconButton } from '@material-ui/core'
import { useUsersFilters } from '../root/hooks/use-users-filters'
import {
    urlToSearchState,
    useSyncUrlState
} from '../../utils/hooks/useSyncUrlState'
import { mdiAccountArrowLeftOutline, mdiAccountCircleOutline } from '@mdi/js'
import MdiIcon from '@mdi/react'
import { Highlighted } from '../root/components/common/highlighted'
import { DialogOpener } from '../root/components/common/dialog-opener'
import { CreateUserModal } from './components/create-user/create-user-modal'
import { PersonAddOutlined } from '@material-ui/icons'
import { MenuComponent } from 'web/src/app/root/components/common/menu'
import { mdiEmailOpenOutline } from '@mdi/js'
import { mdiDeleteOutline } from '@mdi/js'
import { InvitationConfirmModal } from './components/invitation-confirm-modal'
import { newUserSetupEmailRequestHandler } from './api/api'
import { getFileForExport } from './api/users-api'
import { downloadFile } from '../../utils'
import { LOCAL_LOCALE_KEY } from '../root/constants'
import { enqueueSnackbar } from '../root/actions'
import { ExportMenu } from '../root/components/common/export-menu'
import { UserRoundPlusIcon } from 'lucide-react'
const customerOptionsSelector = createSelector(
    state => state.main.p,
    state => state.userManagement.customers,
    (p, customers) => getCustomerOptions(p, customers)
)

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: 'calc(100% - 70px)'
    },
    userNameLink: {
        ...theme.link
    },
    actions: {
        display: 'flex',
        justifyContent: 'center'
    },
    deleteIcon: {
        color: theme.primary,
        cursor: 'pointer'
    },
    userDeleteRow: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 15
    },
    titleArea: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flex: 2
    },
    pageTitle: {
        justifyContent: 'space-between'
    },
    searchInput: {
        width: '35%',
        marginRight: 20
    },
    searchClearIcon: {
        padding: 0,
        position: 'absolute',
        right: 14
    }
}))

function Users(props) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const location = useLocation()

    const isNewUserModalOpen = location?.state?.newUser

    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [usersToDelete, setUsersToDelete] = useState([])
    const [createUserModalOpen, setCreateUserModelOpen] = useState(false)
    const [customerForUser, setCustomerForUser] = useState('')
    const [
        addFromAnotherCustomerOpen,
        setAddFromAnotherCustomerOpen
    ] = useState(false)
    const [isExporting, setIsExporting] = useState(false)

    const [filters, d] = useUsersFilters(urlToSearchState(location))
    useSyncUrlState(filters)

    const { users, roles, isLoading } = useSelector(
        state => state.userManagement
    )
    const loggedInUser = useSelector(state => state.main.user)
    const { p, locale, theme } = useSelector(state => state.main)
    const customerOptions = useSelector(customerOptionsSelector)
    const [invitationConfirmModal, setInvitationConfirmModal] = useState(false)
    const [userInvitationData, setUserInvitationData] = useState(null)

    const columns = [
        {
            key: 'user-icon',
            label: null,
            sortable: false,
            dataType: 'custom',
            width: 0.2,
            render: ({ rowData, cellData }) => {
                if (rowData.is_external) {
                    return (
                        <Tooltip
                            hover
                            placement="top"
                            text={locale.userManagement.externalUser}
                        >
                            <MdiIcon
                                path={mdiAccountArrowLeftOutline}
                                size={'20px'}
                                color={theme.quaternary}
                            />
                        </Tooltip>
                    )
                }
                return (
                    <Tooltip
                        hover
                        placement="top"
                        text={locale.userManagement.internalUser}
                    >
                        <MdiIcon
                            path={mdiAccountCircleOutline}
                            size={'20px'}
                            color={theme.primary}
                        />
                    </Tooltip>
                )
            }
        },
        {
            key: 'name',
            label: locale.common.name,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            render: ({ rowData, cellData }) => (
                <NavLink
                    className={classes.userNameLink}
                    to={`/user-management/users/${rowData.id}`}
                >
                    <Highlighted text={cellData} highlight={filters.search} />
                </NavLink>
            )
        },
        {
            key: 'email',
            label: locale.common.email,
            sortable: true,
            width: 1.5,
            dataType: 'string',
            render: ({ rowData, cellData }) => (
                <Highlighted text={cellData} highlight={filters.search} />
            )
        },
        {
            key: 'original_customer_name',
            label: locale.userManagement.customer,
            sortable: true,
            width: 1.5,
            dataType: 'string',
            render: ({ rowData, cellData }) => (
                <div>{rowData.original_customer_name}</div>
            )
        },
        {
            key: 'role_code',
            label: locale.userManagement.roleName,
            sortable: false,
            width: 1.5,
            dataType: 'string',
            render: ({ rowData, cellData }) => {
                return getRoleName(
                    {
                        name: rowData.role_name,
                        code: rowData.role_code
                    },
                    locale
                )
            }
        },
        {
            key: 'actions',
            label: locale.userManagement.actions,
            sortable: false,
            dataType: 'custom',
            width: 1.5,
            alignCenter: true,
            render: ({ rowData, cellData }) => {
                const hasDeletePermission =
                    UserPermissions.user(rowData.customer_id).delete &&
                    rowData.id !== loggedInUser.id

                return (
                    <div className={classes.actions}>
                        <Tooltip
                            hover
                            placement="top"
                            text={
                                hasDeletePermission
                                    ? ''
                                    : locale.userManagement
                                          .youCanNotDeleteThisUser
                            }
                        >
                            <MenuComponent
                                options={[
                                    {
                                        content: (
                                            <>
                                                <MdiIcon
                                                    path={mdiDeleteOutline}
                                                    size={'20'}
                                                    color={theme.primary}
                                                />
                                                <Box>
                                                    {locale.common.delete}
                                                </Box>
                                            </>
                                        ),

                                        onClick: () => onDeleteClick([rowData]),
                                        hide: !hasDeletePermission
                                    },
                                    {
                                        content: (
                                            <>
                                                <MdiIcon
                                                    path={mdiEmailOpenOutline}
                                                    size={'20'}
                                                    color={theme.primary}
                                                />
                                                <Box>
                                                    {
                                                        locale.userManagement
                                                            .sendInvitationMail
                                                    }
                                                </Box>
                                            </>
                                        ),
                                        onClick: () => {
                                            setInvitationConfirmModal(true)
                                            setUserInvitationData({
                                                email: rowData.email,
                                                customer_name:
                                                    rowData.customer_name
                                            })
                                        }
                                    }
                                ]}
                            />
                        </Tooltip>
                    </div>
                )
            }
        }
    ]

    const prepareCustomerOptions = customers => {
        const options = customers.reduce((acc, c) => {
            acc[c.customer_id] = c.customer_name
            return acc
        }, {})
        return options
    }

    const onDeleteClick = users => {
        setUsersToDelete(
            users.map(u => {
                /* u.customer_id_delete = u.customers[0].customer_id
            u.customersOpts = prepareCustomerOptions(u.customers) */
                u.customer_id_delete = u.customer_id
                u.customersOpts = [
                    {
                        [u.customer_id]: u.customer_name
                    }
                ]
                return u
            })
        )
        setDeleteModalOpen(true)
    }

    const goToNewUser = (cName, cId, allRoles) => {
        const role = allRoles.find(r => r.customer_id === Number(cId))
        dispatch(userSuccess(getNewUser(role.id, cId, role.code, cName)))
        props.history.push('/user-management/users/new')
    }

    const onCreateNewUserClick = (p, user, customerOptions, roles) => {
        if (UserPermissions.isTraplinkedAdmin()) {
            setCustomerForUser(Object.keys(customerOptions)[0])
            setCreateUserModelOpen(true)
        } else {
            goToNewUser(
                user.original_customer_name,
                user.original_customer_id,
                roles
            )
        }
    }

    const onAddFromAnotherCustomer = () => {
        setAddFromAnotherCustomerOpen(true)
    }

    const onSearch = value => {
        d({ type: 'search', value: value.trim() })
    }

    const fetchUsers = () => dispatch(usersRequest(filters))

    useEffect(() => {
        fetchUsers()
        dispatch(rolesRequest())
        if (UserPermissions.customer().read) {
            dispatch(customersRequest())
        }
        return () => {
            dispatch(usersSuccess(null))
            dispatch(rolesRequestSuccess(null))
            dispatch(customersRequestSuccess(null))
        }
    }, [])

    useEffect(() => {
        fetchUsers()
    }, [filters])

    const exportUsers = async fileType => {
        setIsExporting(true)
        const locale_key = localStorage.getItem(LOCAL_LOCALE_KEY)
        const params = {
            ...filters,
            fileType,
            locale_key
        }

        const blob = await getFileForExport(params)
        if (blob) {
            const fileName = `users-export.${fileType}`
            downloadFile(blob, fileType, fileName)
        } else {
            dispatch(
                enqueueSnackbar({
                    message: locale.sagasMsgs.getDevicesEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        setIsExporting(false)
    }
    return (
        <>
            {isExporting && (
                <div className={classes.loader}>
                    <Loading />
                </div>
            )}

            <div className={classes.wrapper}>
                <PageTitle
                    title={locale.userManagement.users}
                    className={classes.pageTitle}
                >
                    <div className={classes.titleArea}>
                        <ExportMenu>
                            {({ handleClose }) => (
                                <div>
                                    <ExportMenu.Csv
                                        locale={locale}
                                        onExport={fileType => {
                                            exportUsers(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Excel
                                        locale={locale}
                                        onExport={fileType => {
                                            exportUsers(fileType)
                                            handleClose()
                                        }}
                                    />
                                    <ExportMenu.Pdf
                                        locale={locale}
                                        onExport={fileType => {
                                            exportUsers(fileType)
                                            handleClose()
                                        }}
                                    />
                                </div>
                            )}
                        </ExportMenu>
                        <Input
                            value={filters.search}
                            className={classes.searchInput}
                            label={locale.common.search}
                            type="text"
                            setValue={onSearch}
                            debounce={600}
                            endIcon={() =>
                                filters.search ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => onSearch('')}
                                        classes={{
                                            root: classes.searchClearIcon
                                        }}
                                    >
                                        <Clear />
                                    </IconButton>
                                ) : null
                            }
                        />
                        <DialogOpener
                            initialOpen={isNewUserModalOpen}
                            dialog={(handleClose, modalProps) => {
                                return modalProps.open ? (
                                    <CreateUserModal
                                        customerForUser={customerForUser}
                                        setCustomerForUser={setCustomerForUser}
                                        roles={roles}
                                        onClose={() => {
                                            fetchUsers()
                                            handleClose()
                                        }}
                                        customerOptions={customerOptions}
                                        modalProps={modalProps}
                                    />
                                ) : null
                            }}
                        >
                            {handleOpen => (
                                <Tooltip
                                    hover
                                    placement="top"
                                    text={locale.userManagement.createNew}
                                >
                                    <IconButton
                                        disabled={
                                            !UserPermissions.user().create
                                        }
                                        onClick={handleOpen}
                                    >
                                        <UserRoundPlusIcon size={20} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </DialogOpener>
                        {/* <Button
                        disabled={
                            !UserPermissions.user().create
                        }
                        onClick={onAddFromAnotherCustomer}
                    >
                        {locale.userManagement.addFromAnotherOrg}
                    </Button> */}
                    </div>
                </PageTitle>

                {users && users.length > 0 && (
                    <TableVirtualized
                        data={users}
                        isLoading={isLoading}
                        columns={columns}
                        sorting={filters.sort_by}
                        onSort={sort_by => d({ type: 'sort', sort_by })}
                    />
                )}
                {!isLoading && (!users || users.length === 0) && (
                    <div>{locale.userManagement.noUsersFound}</div>
                )}

                {/*
                delete modal window
            */}
                <Modal open={deleteModalOpen} setOpen={setDeleteModalOpen}>
                    <DeleteUsersModal
                        setOpen={setDeleteModalOpen}
                        usersToDelete={usersToDelete}
                        setUsersToDelete={setUsersToDelete}
                    />
                </Modal>
                {invitationConfirmModal && (
                    <InvitationConfirmModal
                        open={invitationConfirmModal}
                        setOpen={setInvitationConfirmModal}
                        onSendClick={() =>
                            newUserSetupEmailRequestHandler({
                                userInvitationData,
                                dispatch,
                                successMsg: locale.sagasMsgs.sendInvitationRes,
                                errorMsg: locale.sagasMsgs.sendInvitationEx
                            })
                        }
                    />
                )}

                {/*
                --------***--------
            */}

                {/*
                choose customer for a user
            */}

                {/*
                -----------***------------
            */}

                {/*
                add from another customer
            */}
                <Modal
                    open={addFromAnotherCustomerOpen}
                    setOpen={setAddFromAnotherCustomerOpen}
                >
                    <AddFromCustomerModal
                        setOpen={setAddFromAnotherCustomerOpen}
                        roles={roles}
                    />
                </Modal>
                {/*
                -----------***------------
            */}
            </div>
        </>
    )
}

export default withRouter(Users)
